import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { getLoggedInParticipant } from '../api/participantAuth';
import { StoreContext } from '../../../store';
import { useParticipants } from '../hooks/useParticipants';
import ParticipantFilters from '../components/ParticipantFilters';
import StatsCard from '../components/StatsCard';
import WelcomeBanner from '../components/WelcomeBanner';
import BackToDashboardButton from '../components/BackToDashboardButton';
import { ParticipantRoles } from '../types';
import '../components/ParticipantCard.css';

const RaceParticipantsPage = () => {
  const history = useHistory();
  const store = useContext(StoreContext);
  const { participants: storeParticipants, loading: storeLoading, error: storeError, loadParticipants } = useParticipants();
  const [filteredParticipants, setFilteredParticipants] = useState([]);
  const [stats, setStats] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(null);
  
  // Filter states - no default filter applied
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        await loadParticipants();
      } catch (err) {
      }
    };
    loadData();
  }, []); // Only run on mount

  useEffect(() => {
    if (storeParticipants.length > 0) {
      setFilteredParticipants(storeParticipants);
      calculateStats(storeParticipants);
    }
  }, [storeParticipants]);

  // Helper to check if participant has a specific role
  const hasRole = (participant, roleToCheck) => {
    if (Array.isArray(participant.role)) {
      return participant.role.some(r => r.toLowerCase() === roleToCheck.toLowerCase());
    }
    return participant.role?.toLowerCase() === roleToCheck.toLowerCase();
  };

const calculateStats = (data) => {
  const stats = {
    totalParticipants: data.length,
    totalHandlers: 0,
    totalBreeders: 0,

    // ✅ NEW: breeder birds total (from breederInfo)
    totalBirdsForRace: 0,

    bySection: {
      NORTH: { handlers: 0, totalBirds: 0, reserved: 0, openSlots: 0 },
      CENTRAL: { handlers: 0, totalBirds: 0, reserved: 0, openSlots: 0 },
      SOUTH: { handlers: 0, totalBirds: 0, reserved: 0, openSlots: 0 }
    }
  };

  const breederBirdFields = [
    "totalBirdsForRace",
    "totalBirdForRace",   // typo variant
    "totalBirdsForRaces",
    "totalBirdsRace",
    "totalBirds"          // last-resort if you ever stored it this way
  ];

  const toNumber = (v) => {
    if (typeof v === "number") return Number.isFinite(v) ? v : 0;
    if (typeof v === "string") {
      const n = Number(v.trim());
      return Number.isFinite(n) ? n : 0;
    }
    return 0;
  };

  data.forEach(participant => {
    // Count breeders by role only
    if (hasRole(participant, "breeder")) {
      stats.totalBreeders++;

      // ✅ NEW: add breeder birds-for-race
      const breederInfo = participant?.breederInfo ?? participant?.BreederInfo ?? participant?.breederinfo;
      if (breederInfo && typeof breederInfo === "object") {
        for (const field of breederBirdFields) {
          if (field in breederInfo) {
            stats.totalBirdsForRace += toNumber(breederInfo[field]);
            break; // take first match only
          }
        }
      }
    }

    // Count handlers by role only (do not require handlerInfo)
    if (hasRole(participant, "handler")) {
      stats.totalHandlers++;
    }

    // Calculate handler stats by section (only if handlerInfo and section present)
    if (hasRole(participant, "handler") && participant.handlerInfo) {
      const section = participant.handlerInfo.section?.toUpperCase();
      if (section && stats.bySection[section]) {
        stats.bySection[section].handlers++;
        const totalBirds = participant.handlerInfo.totalBirds || 0;
        const reserved = participant.handlerInfo.reserved || 0;
        const assignedBreeders = participant.handlerInfo.breeders?.length || 0;
        const openSlots = Math.max(0, totalBirds - reserved - assignedBreeders);

        stats.bySection[section].totalBirds += totalBirds;
        stats.bySection[section].reserved += reserved;
        stats.bySection[section].openSlots += openSlots;
      }
    }
  });

  setStats(stats);
};

  useEffect(() => {
    applyFilters();
  }, [selectedRole, selectedSection, searchTerm, storeParticipants]);

  const applyFilters = () => {
    let filtered = [...storeParticipants];

    // Filter by role
    if (selectedRole) {
      filtered = filtered.filter(p => hasRole(p, selectedRole));
    }

    // Filter by section (for handlers)
    if (selectedSection) {
      filtered = filtered.filter(p => {
        if (hasRole(p, 'handler')) {
          return p.handlerInfo?.section?.toUpperCase() === selectedSection.toUpperCase();
        }
        if (hasRole(p, 'breeder')) {
          return p.breederInfo?.handlerSection?.toUpperCase() === selectedSection.toUpperCase();
        }
        return false;
      });
    }

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.fullName?.toLowerCase().includes(search) ||
        p.loftName?.toLowerCase().includes(search) ||
        p.emailAddress?.toLowerCase().includes(search) ||
        p.mobile?.includes(search)
      );
    }

    // Sort by name (A-Z)
    filtered.sort((a, b) => a.fullName.localeCompare(b.fullName));

    setFilteredParticipants(filtered);
  };

  if (storeLoading) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading participants...</p>
        </div>
      </div>
    );
  }

  if (storeError) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error</h4>
          <p>{storeError}</p>
          <button className="btn btn-danger" onClick={() => loadParticipants(true)}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <WelcomeBanner />
      <div className="container-fluid py-4">
      <div className="row justify-content-center">
        <div className="col-12 col-xl-11">
          {/* Header */}
          <div className="text-center mb-4">
            <h1 className="display-5 mb-2" style={{ color: '#0b3d91' }}>
              Race Participants Directory
            </h1>
            <p className="text-muted">Complete list of handlers and breeders for the 2026 Convention Race</p>
          </div>

          {/* Stats Overview */}
          <StatsCard stats={stats} />
          


          {/* Filters */}
          <ParticipantFilters
            selectedRole={selectedRole}
            onRoleChange={setSelectedRole}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            selectedSection={selectedSection}
            onSectionChange={setSelectedSection}
          />

          {/* Results Count */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">
              Showing {filteredParticipants.length} of {storeParticipants.length} participants
            </h5>
            {(selectedRole || selectedSection || searchTerm) && (
              <button 
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  setSelectedRole('');
                  setSelectedSection('');
                  setSearchTerm('');
                }}
              >
                Clear Filters
              </button>
            )}
          </div>

          {/* Participants Table/Cards */}
          {filteredParticipants.length === 0 ? (
            <div className="alert alert-info text-center">
              <p className="mb-0">No participants found matching your filters.</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="card shadow-sm participant-table-desktop">
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-striped mb-0">
                      <thead className="table-dark">
                        <tr>
                          <th className="px-3 py-4 fw-bold">Name</th>
                          <th className="px-3 py-4 fw-bold">Loft Name</th>
                          <th className="px-3 py-4 fw-bold">Role</th>
                          <th className="px-3 py-4 fw-bold">Section</th>
                          <th className="px-3 py-4 fw-bold">Distance</th>
                          <th className="px-3 py-4 fw-bold">Status</th>
                          <th className="px-3 py-4 fw-bold">Contact Info</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredParticipants.map((participant) => (
                          <tr key={participant.id}>
                            <td className="px-3 py-3">
                              <span className="fw-semibold text-dark">{participant.fullName}</span>
                            </td>
                            <td className="px-3 py-3">
                              <span className="text-dark">
                                {participant.loftName || <span className="text-muted fst-italic">No loft name</span>}
                              </span>
                            </td>
                            <td className="px-3 py-3">
                              <span className="text-dark">
                                {Array.isArray(participant.role) 
                                  ? participant.role.join(', ')
                                  : participant.role || 'N/A'
                                }
                              </span>
                            </td>
                            <td className="px-3 py-3">
                              <span className="fw-medium">
                                {hasRole(participant, 'handler') && participant.handlerInfo?.section 
                                  ? participant.handlerInfo.section 
                                  : hasRole(participant, 'breeder') && !hasRole(participant, 'handler')
                                  ? <span className="text-muted fst-italic">N/A</span>
                                  : hasRole(participant, 'breeder') && participant.breederInfo?.handlerSection
                                  ? participant.breederInfo.handlerSection
                                  : <span className="text-muted fst-italic">No section</span>
                                }
                              </span>
                            </td>
                            <td className="px-3 py-3">
                              <span className="text-dark">
                                {hasRole(participant, 'handler') && (participant.handlerInfo?.distanceFromRaceStation || participant.handlerInfo?.distance)
                                  ? (() => {
                                      const distance = participant.handlerInfo.distanceFromRaceStation || participant.handlerInfo.distance;
                                      return String(distance).replace(/ miles?$/i, '').replace(/ mi$/i, '');
                                    })()
                                  : hasRole(participant, 'handler') && !(participant.handlerInfo?.distanceFromRaceStation || participant.handlerInfo?.distance)
                                  ? <span className="text-danger fw-bold">Distance not set</span>
                                  : hasRole(participant, 'breeder') && !hasRole(participant, 'handler')
                                  ? <span className="text-muted fst-italic">N/A</span>
                                  : <span className="text-muted fst-italic">Not specified</span>
                                }
                              </span>
                            </td>
                            <td className="px-3 py-3">
                              <span className="text-dark">
                                {hasRole(participant, 'handler') && participant.handlerInfo ? (
                                  !participant.handlerInfo.totalBirds || participant.handlerInfo.totalBirds === 0 
                                    ? <span className="text-danger fw-bold">Capacity Not Set (Contact Handler)</span> 
                                    : (() => {
                                        const totalBirds = participant.handlerInfo.totalBirds || 0;
                                        const reserved = participant.handlerInfo.reserved || 0;
                                        const assignedBreeders = participant.handlerInfo.breeders?.length || 0;
                                        const openSlots = Math.max(0, totalBirds - reserved - assignedBreeders);
                                        return openSlots > 0 ? (
                                          <span 
                                            className="d-block fw-bold cursor-pointer w-100 h-100 py-2 text-center btn btn-success"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const authData = getLoggedInParticipant();
                                              if (authData && authData.participantId) {
                                                // Redirect to dashboard, Breeder tab (tab=2)
                                                history.push('/race/dashboard?tab=2');
                                              } else {
                                                history.push('/race/select-login');
                                              }
                                            }}
                                            title="Click to login and select this handler"
                                            style={{ cursor: 'pointer'}}
                                          >
                                            Accepting
                                          </span>
                                        ) : 'Full';
                                      })()
                                ) : hasRole(participant, 'breeder') ? (
                                  'N/A'
                                ) : (
                                  'N/A'
                                )}
                              </span>
                            </td>
                            <td className="px-3 py-3">
                              <div>
                                {participant.hideContactInfo && sessionStorage.getItem('participantAuth') == null ? (
                                  <span className="text-muted fst-italic">
                                    <i className="bi bi-lock me-1"></i>
                                    Login required to view contact info
                                  </span>
                                ) : (
                                  <>
                                    {participant.emailAddress && (
                                      <div className="mb-1">
                                        <small className="text-muted me-2">Email:</small>
                                        <a href={`mailto:${participant.emailAddress}`} className="text-decoration-none">
                                          {participant.emailAddress}
                                        </a>
                                      </div>
                                    )}
                                    {participant.mobile && (
                                      <div>
                                        <small className="text-muted me-2">Phone:</small>
                                        <a href={`tel:${participant.mobile}`} className="text-decoration-none">
                                          {participant.mobile}
                                        </a>
                                      </div>
                                    )}
                                    {!participant.emailAddress && !participant.mobile && (
                                      <span className="text-muted fst-italic">No contact info</span>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Mobile Cards */}
              <div className="participant-card-mobile">
                {filteredParticipants.map((participant) => {
                  const roleClass = (() => {
                    const roles = Array.isArray(participant.role) ? participant.role : [participant.role || ''];
                    if (roles.includes('handler') && roles.includes('breeder')) return 'role-both';
                    if (roles.includes('handler')) return 'role-handler';
                    if (roles.includes('breeder')) return 'role-breeder';
                    return 'role-handler';
                  })();

                  const status = hasRole(participant, 'handler') && participant.handlerInfo ? (
                    !participant.handlerInfo.totalBirds || participant.handlerInfo.totalBirds === 0 
                      ? { text: 'Capacity Not Set (Contact Handler)', class: 'status-na' }
                      : (() => {
                          const totalBirds = participant.handlerInfo.totalBirds || 0;
                          const reserved = participant.handlerInfo.reserved || 0;
                          const assignedBreeders = participant.handlerInfo.breeders?.length || 0;
                          const openSlots = Math.max(0, totalBirds - reserved - assignedBreeders);
                          return openSlots > 0 
                            ? { 
                                text: 'Accepting',
                                class: 'status-accepting status-clickable',
                                onClick: (e) => {
                                  e.stopPropagation();
                                  history.push('/race/select-login');
                                }
                              }
                            : { text: 'Full', class: 'status-full' };
                        })()
                  ) : { text: 'N/A', class: 'status-na' };

                  return (
                    <div key={participant.id} className="participant-card">
                      <div className="participant-card-header">
                        <div>
                          <h6 className="participant-name">{participant.fullName}</h6>
                          {participant.loftName && (
                            <small className="text-muted">{participant.loftName}</small>
                          )}
                        </div>
                        <span className={`participant-role ${roleClass}`}>
                          {Array.isArray(participant.role) 
                            ? participant.role.join(', ')
                            : participant.role || 'N/A'
                          }
                        </span>
                      </div>

                      <div className="participant-info">
                        <div className="info-row">
                          <span className="info-label">Section:</span>
                          <span className="info-value">
                            {hasRole(participant, 'handler') && participant.handlerInfo?.section 
                              ? participant.handlerInfo.section 
                              : hasRole(participant, 'breeder') && participant.breederInfo?.handlerSection
                              ? participant.breederInfo.handlerSection
                              : <span className="text-muted">N/A</span>
                            }
                          </span>
                        </div>

                        {hasRole(participant, 'handler') && (
                          <>
                            <div className="info-row">
                              <span className="info-label">Distance:</span>
                              <span className="info-value">
                                {(participant.handlerInfo?.distanceFromRaceStation || participant.handlerInfo?.distance)
                                  ? (() => {
                                      const distance = participant.handlerInfo.distanceFromRaceStation || participant.handlerInfo.distance;
                                      return String(distance).replace(/ miles?$/i, '').replace(/ mi$/i, '');
                                    })()
                                  : <span className="text-danger">Not set</span>
                                }
                              </span>
                            </div>

                            <div className="info-row">
                              <span className="info-label">Status:</span>
                              <span 
                                className={`info-value status-badge ${status.class}`}
                                onClick={status.onClick || undefined}
                                style={status.onClick ? { cursor: 'pointer' } : undefined}
                                title={status.onClick ? "Click to login and select this handler" : undefined}
                              >
                                {status.text}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    <div className="contact-info">
                      {participant.hideContactInfo && sessionStorage.getItem('participantAuth') == null ? (
                        <div className="contact-item text-muted">
                          <i className="bi bi-lock"></i>
                          Login required to view contact info
                        </div>
                      ) : (participant.emailAddress || participant.mobile) ? (
                        <>
                          {participant.emailAddress && (
                            <div className="contact-item">
                              <i className="bi bi-envelope"></i>
                              <a href={`mailto:${participant.emailAddress}`}>
                                {participant.emailAddress}
                              </a>
                            </div>
                          )}
                          {participant.mobile && (
                            <div className="contact-item">
                              <i className="bi bi-telephone"></i>
                              <a href={`tel:${participant.mobile}`}>
                                {participant.mobile}
                              </a>
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="text-muted fst-italic"></span>
                      )}
                    </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Navigation Buttons */}
          <div className="mt-5">
            <div className="d-flex flex-column flex-md-row gap-3 justify-content-center">
              <a href="/race/signup" className="btn btn-primary w-100 w-md-auto" style={{ minHeight: '44px' }}>
                <i className="bi bi-person-plus me-2"></i>
                Register Now
              </a>
              <a href="/race/select-login" className="btn btn-success w-100 w-md-auto" style={{ minHeight: '44px' }}>
                <i className="bi bi-box-arrow-in-right me-2"></i>
                Login
              </a>
            </div>
            <BackToDashboardButton />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default RaceParticipantsPage;
